import { Notification } from '../types';
import { USER_TYPES } from './constants';

export interface NotificationTarget {
  to: string;
  // location state forwarded to the destination page (e.g. to auto-open a details modal)
  state?: Record<string, any>;
}

// Key used to carry a row-like payload that a destination page can feed straight
// into its existing details modal (`handleDialogOpen`).
export const NOTIFICATION_DETAIL_STATE_KEY = 'openDetailFromNotification';

// Build a row-like object for the inquiry / sales-query details modals from
// whatever the notification metadata happens to carry. Missing fields simply
// render as empty rows in those modals, so partial data is fine.
const buildInquiryDetail = (note: Notification) => {
  const meta = note.metadata ?? {};
  return {
    id: note.entityId ?? meta.inquiryId ?? meta.salesInquiryId,
    fullName: meta.customerName ?? meta.fullName ?? meta.name,
    name: meta.customerName ?? meta.name,
    email: meta.customerEmail ?? meta.email,
    phoneNumber: meta.phoneNumber ?? meta.phone,
    subject: meta.subject,
    message: meta.message,
    source: meta.source,
    createdAt: note.createdAt,
  };
};

/**
 * Resolve where a notification should navigate when clicked, plus any location
 * state needed to open a details modal on the destination page.
 *
 * Role aware: a few destinations differ between the supervisor and agent route
 * trees (leave / swap requests). Everything else is shared.
 */
export const resolveNotificationTarget = (
  note: Notification,
  userType?: number
): NotificationTarget => {
  const type = note.type?.toLowerCase();
  const meta = note.metadata ?? {};
  const isAgent = userType === USER_TYPES.AGENT;

  switch (type) {
    case 'ticket_assigned':
    case 'ticket_reassigned': {
      const ticketId = note.entityId ?? note.ticketId ?? meta.ticketId;
      return ticketId ? { to: `/tickets/${ticketId}` } : { to: '/tickets' };
    }

    case 'deal_assigned':
    case 'deal_created':
      return { to: '/crm/sales' };

    case 'leave_request':
    case 'leave_request_approved':
    case 'leave_request_rejected':
      return { to: isAgent ? '/leave-requests' : '/all-leave-requests' };

    case 'shift_swap_request':
    case 'shift_swap_approved':
    case 'shift_swap_rejected':
      return { to: isAgent ? '/shift-swaps' : '/all-swap-requests' };

    case 'customer_update_request': {
      // Only carry detail state when the metadata is rich enough to render the
      // request modal (it needs the customer / changeData payload).
      const hasDetail = !!(meta.changeData || meta.customer);
      return {
        to: '/crm/requests',
        state: hasDetail
          ? { [NOTIFICATION_DETAIL_STATE_KEY]: { ...meta, id: meta.id ?? note.entityId } }
          : undefined,
      };
    }

    case 'sales_inquiry_created':
      return {
        to: '/crm/sales-query',
        state: { [NOTIFICATION_DETAIL_STATE_KEY]: buildInquiryDetail(note) },
      };

    case 'query_inquiry_created':
      return {
        to: '/customer-query',
        state: { [NOTIFICATION_DETAIL_STATE_KEY]: buildInquiryDetail(note) },
      };

    case 'invoice_success':
    case 'grace_period_start':
    case 'trial_reminder':
      return { to: '/subscription' };

    default:
      // Unknown type: fall back to the legacy ticket behaviour, otherwise stay
      // on the notifications list.
      if (note.entityType === 'ticket' && (note.entityId ?? note.ticketId)) {
        return { to: `/tickets/${note.entityId ?? note.ticketId}` };
      }
      return { to: '/notifications' };
  }
};
